<template>
  <div>
    <modal class="fm-recording-download-modal" v-if="downloadModalVisible">
      <template slot="header">
        <h4 class="modal-title">Download Recording</h4>
        <md-button
          class="md-simple md-just-icon md-round modal-default-button"
          @click="closeModal"
        >
          <md-icon>clear</md-icon>
        </md-button>
      </template>
      <template slot="body">
        <div class="md-layout">
          <div class="md-layout-item md-size-100">
            You can download a muxed recording file with every participants
            audio and video together, or an unmuxed zip file of individual audio
            and videos files to combine your own way.<br /><br />
          </div>
          <div v-if="unmuxedDownloadModalVisible">
            Downloading unmuxed recordings...<br/>
            <md-progress-bar
              md-mode="determinate"
              :md-value="unmuxedProgressBar.downloadPercentage"
            ></md-progress-bar>
          </div>
          <md-button
            v-else
            class="md-primary md-raised"
            @click.stop="handleConnectionsDownload(selectedRecording, true)"
          >
            Download unmuxed recordings
          </md-button>

          <div v-if="muxedDownloadModalVisible">
            Downloading muxed recordings...<br/>
            <md-progress-bar
              md-mode="determinate"
              :md-value="muxedProgressBar.downloadPercentage"
            ></md-progress-bar>
          </div>
          <md-button
            v-else
            class="md-primary md-raised"
            @click.stop="handleChannelRecordingDownload(selectedRecording)"
            >Download muxed recording</md-button
          >
        </div>
      </template>
      <template slot="footer">
        <md-button class="md-danger md-simple" @click.stop="closeModal"
          >Cancel</md-button
        >
      </template>
    </modal>
  </div>
</template>

<script>
import { Modal } from "@/components";
import api from "@/api";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import fileDownloader from "../../utils/fileDownloader.js";
import swal from "sweetalert2";

export default {
  components: {
    Modal,
  },
  props: ["selectedRecording", "downloadModalVisible"],
  data: () => ({
    fileDownloader: new fileDownloader(),
    muxedDownloadModalVisible: false,
    unmuxedDownloadModalVisible: false,
    startingConnectionRecordingsDownload: false,
    unmuxedProgressBar: {
      recordingsDownloaded: 0, //current value
      totalRecordingsToDownload: 0, //maximum value
      downloadPercentage: 0,
    },
    muxedProgressBar: {
      recordingAmountDownloaded: 0, //current value
      expectedTotalDownloadSize: 0, //maximum value
      downloadPercentage: 0,
    },
  }),
  methods: {
    //Unmuxed progress bar is based on portion of connection recording audio/video files that have finished downloading.
    handleUnmuxedDownloadProgress(loadEndEvent) {
      this.unmuxedProgressBar.recordingsDownloaded++;
      this.unmuxedProgressBar.downloadPercentage =
        100 *
        (this.unmuxedProgressBar.recordingsDownloaded /
          this.unmuxedProgressBar.totalRecordingsToDownload);
    },
    //Muxed progress bar is based on portion of xml http request Content-Length that has finished downloading.
    handleMuxedDownloadProgress(progressUpdate) {
      if (progressUpdate.lengthComputable) {
        this.muxedProgressBar.expectedTotalDownloadSize = progressUpdate.total;
        this.muxedProgressBar.recordingAmountDownloaded = progressUpdate.loaded;
        this.muxedProgressBar.downloadPercentage =
          100 *
          (this.muxedProgressBar.recordingAmountDownloaded /
            this.muxedProgressBar.expectedTotalDownloadSize);
      }
    },
    closeModal: function () {
      let me = this;
      if (
        this.muxedDownloadModalVisible == true ||
        this.unmuxedDownloadModalVisible == true
      ) {
        swal
          .fire({
            title: "Cancel Download?",
            text: `Exiting now will cancel the download in progress!`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonClass: "md-button md-success btn-fill",
            cancelButtonClass: "md-button md-danger btn-fill",
            confirmButtonText: "Yes, cancel it!",
            cancelButtonText: "Go Back",
            buttonsStyling: false,
            allowOutsideClick: false,
          })
          .then((result) => {
            if (result) {
              if (result.value) {
                me.fileDownloader.abortAll();
                me.closeUnmuxedModal();
                me.closeMuxedModal();
                me.$emit("closeDownloadModal", false, false);
              } 
            }
          });
      } else {
        this.$emit("closeDownloadModal", false, false);
      }
    },
    //SaveFiles
    //Input: an array of urls to s3 buckets containing mka audio and mkv video files
    //Function: downloads a single zip file containing all those files.
    saveFiles(connectionRecordingUrls) {
      let zip = new JSZip();
      let urlPromises = [];
      let fileNames = [];
      let me = this;
      me.unmuxedProgressBar.totalRecordingsToDownload =
        connectionRecordingUrls.length;
      me.startingConnectionRecordingsDownload = true;
      connectionRecordingUrls.forEach((connectionRecordingUrl) => {
        urlPromises.push(
          this.fileDownloader.getBlob(
            connectionRecordingUrl,
            null,
            this.handleUnmuxedDownloadProgress,
            this.handleUnmuxedDownloadError
          )
        );
        const indexOfQuery =
          connectionRecordingUrl.indexOf("?") == -1
            ? connectionRecordingUrl.length - 1
            : connectionRecordingUrl.indexOf("?");
        const urlWithoutQuery = connectionRecordingUrl.substring(
          0,
          indexOfQuery
        );
        const fileName = urlWithoutQuery.substring(
          urlWithoutQuery.lastIndexOf("/") + 1
        );
        fileNames.push(fileName);
      });
      Promise.all(urlPromises)
        .then(() => {
          let i = 0;
          urlPromises.forEach((recordingBlob) => {
            let folderName = fileNames[i].substring(
              null,
              fileNames[i].lastIndexOf(".") + 1
            );
            let connectionRecordingZipFolder = zip.folder(folderName);
            connectionRecordingZipFolder.file(fileNames[i], recordingBlob);
            i++;
          });
          zip.generateAsync({ type: "blob" }).then(function (blob) {
            saveAs(blob, "connectionRecordings.zip");
            me.closeUnmuxedModal();
          });
        })
        .catch((ex) => {
          me.handleUnmuxedDownloadError(ex);
        });
    },
    closeUnmuxedModal() {
      this.unmuxedDownloadModalVisible = false;
      this.unmuxedProgressBar.totalRecordingsToDownload = 0;
      this.unmuxedProgressBar.downloadPercentage = 0;
      this.unmuxedProgressBar.recordingsDownloaded = 0;
    },
    closeMuxedModal() {
      this.muxedDownloadModalVisible = false;
      this.muxedProgressBar.expectedTotalDownloadSize = 0;
      this.muxedProgressBar.downloadPercentage = 0;
      this.muxedProgressBar.recordingAmountDownloaded = 0;
    },
    handleConnectionsDownload(item, clickedFromModal = false) {
      if (clickedFromModal) {
        this.unmuxedDownloadModalVisible = true;
      }
      api
        .getConnectionRecordingsDownload(
          item.applicationId,
          item.channelId,
          item.id
        )
        .then((connectionRecordings) => {
          this.saveFiles(connectionRecordings);
        })
        .catch((ex) => {
          this.handleUnmuxedDownloadError(ex);
        });
    },
    handleChannelRecordingDownload(item) {
      if (item.ready == true) {
        this.muxedDownloadModalVisible = true;
        api
          .getChannelRecordingUrl(item.applicationId, item.channelId, item.id)
          .then((result) => {
            if (result) {
              this.download(result);
            } else {
              throw new Error("No Recording Found");
            }
          })
          .catch((ex) => {
            this.handleMuxedDownloadError(e);
            console.error(ex);
          });
      }
    },
    download(channelRecordingUrl) {
      let zip = new JSZip();
      let fileName = this.getmp4FileName(channelRecordingUrl);
      let me = this;
      this.fileDownloader
        .getBlob(
          channelRecordingUrl,
          this.handleMuxedDownloadProgress,
          null,
          this.handleMuxedDownloadError
        )
        .then((result) => {
          zip.file(fileName, result);
          zip.generateAsync({ type: "blob" }).then(function (blob) {
            saveAs(blob, fileName);
            me.closeMuxedModal();
          });
        })
        .catch((ex) => {
          console.error(ex);
          me.handleMuxedDownloadError(ex);
        });
    },
    getmp4FileName(link) {
      let fileName = link.substring(0, link.lastIndexOf(".mp4") + 4);
      fileName = fileName.substring(fileName.lastIndexOf("/") + 1);
      return fileName;
    },
    handleUnmuxedDownloadError(e) {
      if (this.startingConnectionRecordingsDownload == true) {
        //ensure only sending 1 alert per zip of connectionRecordings
        this.startingConnectionRecordingsDownload = false;
        this.$store.commit("pushAlert", "Unable to Download Recordings");
      }
      this.fileDownloader.abortAll();
      this.closeUnmuxedModal();
    },
    handleMuxedDownloadError(e) {
      this.$store.commit("pushAlert", "Unable to Download Recording");
      this.fileDownloader.abortAll();
      this.closeMuxedModal();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "src/assets/scss/fm/colours.scss";

.fm-recording-download-modal .md-button.md-primary{
  background-color: $fm-primary-dark-blue !important;
}

.fm-recording-download-modal .md-layout {
  justify-content: inherit;
}
</style>
